import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DesignConfigurator from "@/components/DesignConfigurator";
import { api, formatApiError } from "@/lib/api";
import { PROCESS_STEPS, fabricImg, fmtPrice, videoEmbed } from "@/lib/designOptions";

const EASE = [0.16, 1, 0.3, 1];
const SORTS = [
  { id: "default", label: "Default" },
  { id: "name", label: "Name A–Z" },
  { id: "mill", label: "Mill A–Z" },
];

export default function DesignStudioPage() {
  const [fabrics, setFabrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ color: null, mill: null, pattern: null });
  const [sort, setSort] = useState("default");
  const [selected, setSelected] = useState(null);
  const [studio, setStudio] = useState({ video_url: "", has_upload: false });

  useEffect(() => {
    api
      .get("/fabrics")
      .then((r) => setFabrics(r.data.fabrics))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    api.get("/studio-settings").then((r) => setStudio(r.data)).catch(() => {});
  }, []);

  const groups = useMemo(() => {
    const uniq = (key) => [...new Set(fabrics.map((f) => f[key]))].sort();
    return [
      { id: "color", label: "Color", values: uniq("color") },
      { id: "mill", label: "Fabric Produced By", values: uniq("mill") },
      { id: "pattern", label: "Pattern", values: uniq("pattern") },
    ];
  }, [fabrics]);

  const filtered = useMemo(() => {
    let list = fabrics.filter(
      (f) =>
        (!filters.color || f.color === filters.color) &&
        (!filters.mill || f.mill === filters.mill) &&
        (!filters.pattern || f.pattern === filters.pattern)
    );
    if (sort === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    if (sort === "mill") list = [...list].sort((a, b) => a.mill.localeCompare(b.mill));
    return list;
  }, [fabrics, filters, sort]);

  const activeFilters = Object.entries(filters).filter(([, v]) => v);
  const toggle = (group, value) =>
    setFilters((f) => ({ ...f, [group]: f[group] === value ? null : value }));

  return (
    <div className="bg-[#FFFFFF]" data-testid="design-studio-page">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#121110]">
        <img
          src="/images/groom-suit.jpg"
          alt="Custom tailoring by Bespoke Maestro"
          className="absolute inset-0 h-full w-full object-cover object-top opacity-35"
        />
        <div className="relative mx-auto max-w-7xl px-6 py-24 text-center sm:px-12 sm:py-32 lg:px-20">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#C59B27]"
            data-testid="design-hero-overline"
          >
            The Design Studio · Bespoke Maestro
          </motion.p>
          <h1 className="mt-6 font-serif text-4xl font-light tracking-tight text-white sm:text-5xl lg:text-6xl">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.25, ease: EASE }}
              >
                Design your own
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            className="mx-auto mt-6 max-w-2xl text-base font-light leading-relaxed text-white/75 sm:text-lg"
          >
            Choose your cloth from the world's finest Italian and English mills, then shape a suit,
            sport coat or trousers that exist for no one but you.
          </motion.p>
        </div>
      </section>

      {/* Toolbar */}
      <div className="border-b border-[#E5DEC9]">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-5 sm:px-12 lg:px-20">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#858078]" data-testid="design-showing-count">
            {loading ? "Unrolling the cloth…" : `Showing ${filtered.length} of ${fabrics.length} cloths`}
          </p>
          <label className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#858078]">Sort By</span>
            <select
              data-testid="design-sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="border border-[#E5DEC9] bg-white px-3 py-2 text-xs text-[#1A1917] outline-none focus:border-[#1A1917]"
            >
              {SORTS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {/* Shop */}
      <section className="mx-auto grid max-w-7xl gap-12 px-6 py-14 sm:px-12 lg:grid-cols-12 lg:px-20">
        {/* Layered navigation */}
        <aside className="lg:col-span-3" data-testid="design-filters">
          <h2 className="font-serif text-2xl font-normal tracking-tight text-[#1A1917]">Layered Navigation</h2>
          <div className="mt-2 h-px bg-[#E5DEC9]" />

          {activeFilters.length > 0 && (
            <div className="mt-6 space-y-2" data-testid="design-active-filters">
              {activeFilters.map(([g, v]) => (
                <button
                  key={g}
                  data-testid={`design-active-filter-${g}`}
                  onClick={() => setFilters((f) => ({ ...f, [g]: null }))}
                  className="mr-2 inline-flex items-center gap-2 border border-[#1A1917] px-3 py-1.5 text-xs text-[#1A1917] transition-colors hover:bg-[#1A1917] hover:text-white"
                >
                  {v} <X size={12} />
                </button>
              ))}
              <button
                data-testid="design-clear-filters"
                onClick={() => setFilters({ color: null, mill: null, pattern: null })}
                className="block pt-1 text-xs text-[#8C6D38] underline-offset-4 hover:underline"
              >
                Clear all
              </button>
            </div>
          )}

          {groups.map((g) => (
            <div key={g.id} className="mt-10">
              <h3 className="font-serif text-xl font-normal text-[#1A1917]">{g.label}</h3>
              <ul className="mt-4 space-y-2.5">
                {g.values.map((v) => (
                  <li key={v}>
                    <button
                      data-testid={`design-filter-${g.id}-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => toggle(g.id, v)}
                      className={`text-left text-sm font-light transition-colors ${
                        filters[g.id] === v
                          ? "font-normal text-[#8C6D38]"
                          : "text-[#524F4A] hover:text-[#1A1917]"
                      }`}
                    >
                      {v}
                    </button>
                  </li>
                ))}
              </ul>
              <div className="mt-6 h-px bg-[#EDE8DB]" />
            </div>
          ))}
        </aside>

        {/* Grid */}
        <div className="lg:col-span-9">
          {loading && (
            <p className="py-24 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]">
              Unrolling the cloth…
            </p>
          )}
          {!loading && filtered.length === 0 && (
            <p className="border border-[#E5DEC9] py-24 text-center font-mono text-[11px] uppercase tracking-[0.25em] text-[#858078]" data-testid="design-empty">
              No cloths match these filters
            </p>
          )}
          <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 xl:grid-cols-3" data-testid="design-fabric-grid">
            {filtered.map((f, i) => (
              <motion.button
                key={f.fabric_id}
                data-testid={`design-fabric-${f.fabric_id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: EASE }}
                onClick={() => setSelected(f)}
                className="group text-left"
              >
                <div className="overflow-hidden border border-[#EDE8DB] bg-[#F5F2EA]">
                  {fabricImg(f) ? (
                    <img
                      src={fabricImg(f)}
                      alt={`${f.name} by ${f.mill}`}
                      loading="lazy"
                      className="aspect-square w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex aspect-square w-full items-center justify-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#858078]">
                      Swatch coming soon
                    </div>
                  )}
                </div>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#8C6D38]">{f.mill}</p>
                <h3 className="mt-1 font-serif text-xl font-normal text-[#1A1917]">{f.name}</h3>
                <p className="mt-1 text-xs font-light text-[#858078]">
                  {f.composition} · {f.color}
                </p>
                {f.price_from ? (
                  <p className="mt-1.5 font-mono text-[11px] tracking-[0.1em] text-[#1A1917]" data-testid={`design-fabric-price-${f.fabric_id}`}>
                    Suit from {fmtPrice(f.price_from)}
                  </p>
                ) : null}
                <p className="mt-3 inline-block border-b border-[#1A1917] pb-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#1A1917] opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  Design with this cloth
                </p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="border-t border-[#E5DEC9] bg-[#FBF9F5]" id="mtm-process" data-testid="design-process-section">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-12 lg:px-20">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#858078]">The Made-to-Measure Journey</p>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl font-normal leading-[1.15] tracking-tight text-[#1A1917] sm:text-4xl">
            From cloth to closet — how it works
          </h2>
          <div className="mt-12" data-testid="design-video-block">
            <StudioVideo studio={studio} />
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 xl:grid-cols-4">
            {PROCESS_STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
                className="border border-[#E5DEC9] bg-white p-8"
              >
                <p className="font-mono text-xs tracking-[0.3em] text-[#8C6D38]">{s.n}</p>
                <h3 className="mt-4 font-serif text-xl font-medium text-[#1A1917]">{s.title}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-[#524F4A]">{s.text}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-10 max-w-3xl text-sm font-light leading-relaxed text-[#524F4A]">
            Every commission includes a basted trial garment and a fitting with a local stylist —
            it is how we guarantee a fit that no off-the-rack garment can approach. From your
            measurement session to delivery, please allow up to eight weeks.
          </p>
        </div>
      </section>

      {selected && (
        <DesignConfigurator fabric={selected} onClose={() => setSelected(null)} />
      )}

      <Footer />
    </div>
  );
}

function StudioVideo({ studio }) {
  if (studio.has_upload) {
    return (
      <video
        controls
        preload="metadata"
        data-testid="design-video-player"
        className="aspect-video w-full max-w-4xl bg-black object-contain"
        src={`${process.env.REACT_APP_BACKEND_URL}/api/studio/video`}
      />
    );
  }
  const embed = videoEmbed(studio.video_url);
  if (embed?.type === "iframe") {
    return (
      <iframe
        data-testid="design-video-embed"
        className="aspect-video w-full max-w-4xl"
        src={embed.src}
        title="Our measurement process"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }
  if (embed?.type === "video") {
    return (
      <video controls preload="metadata" data-testid="design-video-player" className="aspect-video w-full max-w-4xl bg-black object-contain" src={embed.src} />
    );
  }
  return (
    <div
      data-testid="design-video-placeholder"
      className="flex aspect-video w-full max-w-4xl flex-col items-center justify-center border border-[#E5DEC9] bg-white px-8 text-center"
    >
      <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#8C6D38]">
        <svg width="18" height="20" viewBox="0 0 18 20" fill="none" aria-hidden="true">
          <path d="M1 1.5v17l16-8.5L1 1.5z" fill="#8C6D38" />
        </svg>
      </span>
      <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C6D38]">Our measurement process on film</p>
      <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-[#524F4A]">
        Coming soon. Until then, Reza walks every client through the measurements personally —
        in person in San Diego, or on a guided video call.
      </p>
    </div>
  );
}
